import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import 'bootstrap/dist/css/bootstrap.min.css';

const SearchableDropdown = (props) => {
  const {
    id,
    label,
    name,
    options = [],
    placeholder,
    onChange,
    multiple = false,
    searchable = false,
    labelClass,
    disabled = false, // Disabled prop
    isDynamic = false,
    required = false,
    value,
    hint,
  } = props;
  // console.log("props", props);

  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [selectedOptions, setSelectedOptions] = useState(
    isDynamic ? [[]] : multiple ? [] : null
  );
  const [showOptions, setShowOptions] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const dropdownRef = useRef(null);
  const hintRef = useRef(null);
  const searchInputRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const labelClasses = `${labelClass || ''} mb-0`;

  useEffect(() => {
    setFilteredOptions(
      options.filter((option) =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [searchTerm, options]);

  useEffect(() => {
    // console.log("useEffect , [value]", value, options,);
    if (value !== "" && value !== null && value !== undefined) {
      let selectValue = null;
      if (multiple) {
        selectValue = options.filter((option) => value.includes(option.value));
      } else {
        selectValue = options.filter((option) => option.value == value);
        selectValue = selectValue[0];
      }
      // console.log("selectValue", selectValue);
      setSelectedOptions(selectValue);
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !event.target.closest('.floating-dropdown')
      ) {
        setShowOptions(false);
      }
      if (hintRef.current && !hintRef.current.contains(event.target)) {
        setShowHint(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (showOptions && searchable && !disabled) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    }
  }, [showOptions, searchable, disabled]);

  const onOptionSelect = (selectedValue) => {
    onChange(id, selectedValue);
  };

  const handleSelect = (option, index) => {
    if (disabled) return; // Prevent selection if disabled

    if (isDynamic && multiple) {
      const updatedOptions = [...selectedOptions];
      updatedOptions[index] = [option];
      setSelectedOptions(updatedOptions);
      onOptionSelect(updatedOptions);
    } else if (multiple) {
      setSelectedOptions((prevSelectedOptions) => {
        let updatedOptions;
        if (prevSelectedOptions.some((selected) => selected.value === option.value)) {
          updatedOptions = prevSelectedOptions.filter(
            (selected) => selected.value !== option.value
          );
        } else {
          updatedOptions = [...prevSelectedOptions, option];
        }
        if (onOptionSelect) {
          onOptionSelect(updatedOptions.map((data) => data.value));
        }
        return updatedOptions;
      });
    } else {
      setSelectedOptions(option);
      setShowOptions(false);
      if (onOptionSelect) {
        onOptionSelect(option.value);
      }
    }
  };

  const handleClearSelection = () => {
    if (disabled) return; // Prevent clearing if disabled
    setSelectedOptions(
      isDynamic && multiple ? [[]] : multiple ? [] : null
    );
    if (onOptionSelect) {
      onOptionSelect(isDynamic && multiple ? [[]] : multiple ? [] : undefined);
    }
  };

  const handleDropdownToggle = () => {
    if (disabled) return; // Prevent dropdown toggle if disabled
    const rect = dropdownRef.current.getBoundingClientRect();
    setDropdownPosition({ top: rect.bottom, left: rect.left, width: rect.width });
    setShowOptions((prev) => !prev);
  };

  const handleAddField = () => {
    if (disabled) return; // Prevent adding fields if disabled
    const updatedOptions = [...selectedOptions, []];
    setSelectedOptions(updatedOptions);
    onOptionSelect(updatedOptions);
  };

  const handleRemoveField = (index) => {
    if (disabled) return; // Prevent removing fields if disabled
    const updatedOptions = selectedOptions.filter((_, idx) => idx !== index);
    setSelectedOptions(updatedOptions);
    onOptionSelect(updatedOptions);
  };

  // console.log("SearchableDropdown", props, selectedOptions);

  return (
    <div className="form-group">
      <div className="d-inline-flex align-items-center gap-2 mb-1 position-relative flex-nowrap">
        <label htmlFor={name} className={labelClasses}>
          {label}
          {required && <span className="text-danger ms-1">*</span>}
        </label>
        {hint && (
          <div className="position-relative" ref={hintRef}>
            <span
              role="button"
              tabIndex={0}
              className="text-muted"
              style={{ cursor: 'pointer' }}
              aria-label="Show hint"
              onClick={() => setShowHint((prev) => !prev)}
              onKeyDown={(e) => e.key === 'Enter' && setShowHint((prev) => !prev)}
            >
              ℹ️
            </span>
            {showHint && (
              <div
                className="position-absolute bg-white border shadow rounded p-2"
                style={{
                  top: '120%',
                  left: 0,
                  width: '260px',
                  zIndex: 3000,
                }}
              >
                <div className="small text-muted">{hint}</div>
              </div>
            )}
          </div>
        )}
      </div>
      <div
        className={`position-relative ${disabled ? 'disabled-dropdown' : ''}`}
        ref={dropdownRef}
      >
        <div
          className={`form-control d-flex align-items-center justify-content-between ${disabled ? 'disabled' : ''
            }`}
          onClick={handleDropdownToggle}
          style={{
            cursor: disabled ? 'not-allowed' : 'pointer',
            backgroundColor: disabled ? '#e9ecef' : 'white',
          }}
        >
          <div>
            {multiple ? (
              selectedOptions.length > 0
                ? selectedOptions.map((opt) => (
                  <span
                    key={opt.value}
                    className="badge bg-primary me-1"
                    style={{ cursor: disabled ? 'not-allowed' : 'default' }}
                  >
                    {opt.label}
                    {!disabled && (
                      <button
                        type="button"
                        className="btn-close btn-close-white ms-1"
                        aria-label="Remove"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelect(opt, 0);
                        }}
                      ></button>
                    )}
                  </span>
                ))
                : placeholder
            ) : (
              selectedOptions?.label || placeholder
            )}
          </div>
          {!multiple && selectedOptions && !disabled && (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={(e) => {
                e.stopPropagation();
                handleClearSelection();
              }}
            >
              Clear
            </button>
          )}
        </div>
        {showOptions &&
          ReactDOM.createPortal(
            <div
              className="floating-dropdown position-fixed bg-white border shadow"
              style={{
                top: dropdownPosition.top,
                left: dropdownPosition.left,
                width: dropdownPosition.width,
                // Keep dropdown above ReactModal overlay/content.
                zIndex: 3001,
                maxHeight: '240px',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                pointerEvents: disabled ? 'none' : 'auto',
              }}
            >
              {searchable && !disabled && (
                <input
                  ref={searchInputRef}
                  type="text"
                  className="form-control"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              )}
              <ul
                className="list-group"
                style={{
                  overflowY: 'auto',
                  flex: 1,
                }}
              >
                {filteredOptions.map((option) => {
                  const isActive = selectedOptions && (multiple
                    ? selectedOptions.some(
                      (selected) => selected.value === option.value
                    )
                    : selectedOptions.value == option.value);
                  return (
                    <li
                      key={option.value}
                      className={`list-group-item ${(isActive)
                        ? 'active'
                        : ''
                        }`}
                      style={{
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        backgroundColor: disabled ? '#f8f9fa' : isActive ? '' : 'white',
                      }}
                      onClick={() => !disabled && handleSelect(option, 0)}
                    >
                      {option.label}
                    </li>
                  )
                })}
              </ul>
            </div>,
            document.body
          )}
      </div>
    </div>
  );
};

export default SearchableDropdown;
